<template lang="pug">
  .yz-upload-card(:class="cardClasses" :style="combinedStyles" @dragover.prevent="handleDragOver" @dragenter.prevent="handleDragEnter" @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop" @click="handleCardClick")
    .yz-upload-card__content
      slot(name="icon")
        .yz-upload-card__icon.mb-3(:style="iconContainerStyles")
          v-icon(v-if="icon" :size="iconSize" :color="iconColor") {{ icon }}

      .yz-upload-card__title.mb-1
        slot(name="title")
          .d-flex.align-center.justify-center
            .yz-heading-10(v-if="primaryTitle" style="color: #0D6EFF") {{ primaryTitle }}
            .yz-regular-body-xs.ml-1(v-if="secondaryTitle") {{ secondaryTitle }}

      .yz-upload-card__description
        slot(name="description")
          .yz-regular-body-xs.text-neutral-500(v-if="description") {{ description }}

    input.yz-upload-input(ref="fileInput" type="file" :accept="accept" :multiple="multiple" @change="onFileChange")
</template>

<script>
import { resizeImage, isFileSizeOverLimit } from '@/utils'
import { mapActions } from 'vuex'

/**
 * YzUploadCard - A flexible upload component with dual upload strategies
 *
 * Upload Modes:
 * - 'preview' (default): Files are processed locally, creates blob URLs for previews
 * - 'immediate': Files are uploaded immediately to API endpoint, has 'hid' field in response
 *
 * Databin Integration:
 * When uploadMode is 'immediate', the component will:
 * 1. Upload all files to uploadUrl endpoint in parallel
 * 2. Wait for all uploads to complete using Promise.allSettled()
 * 3. Emit @upload-batch-complete event with successful upload responses
 * 4. Use YzDatabinSync component externally to handle databin synchronization
 *
 * Usage:
 * Preview Mode:
 *   <yz-upload-card v-model="files" uploadMode="preview" />
 *
 * Immediate Upload Mode:
 *   <yz-upload-card
 *     v-model="uploadedFiles"
 *     uploadMode="immediate"
 *     uploadUrl="/documents/"
 *     :fileSizeLimit="10"
 *     @upload-success="handleSuccess"
 *     @upload-batch-complete="handleBatchComplete"
 *   />
 *
 * With Databin Synchronization:
 *   <template>
 *     <yz-databin-sync
 *       v-model="databinItems"
 *       ref="databinSync"
 *       @databin-created="handleDatabinCreated"
 *     />
 *     <yz-upload-card
 *       v-model="uploadedFiles"
 *       uploadMode="immediate"
 *       uploadUrl="/documents/"
 *       @upload-batch-complete="handleBatchComplete"
 *     />
 *   </template>
 *
 *   methods: {
 *     handleBatchComplete(uploadResults) {
 *       this.$refs.databinSync.addItems(uploadResults)
 *     }
 *   }
 *
 * Events:
 * - @upload-start: When upload begins
 * - @upload-progress: Progress updates during upload
 * - @upload-success: Upload completed successfully
 * - @upload-error: Upload failed
 * - @upload-batch-complete: Batch upload completed (only in immediate mode)
 */
export default {
  name: 'YzUploadCard',
  props: {
    // v-model support
    value: {
      type: Array,
      default: () => []
    },
    // File upload specific props
    multiple: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default: 'image/*'
    },
    appendFiles: {
      type: Boolean,
      default: false
    },
    // Card content props
    icon: {
      type: String,
      default: '$yzi-image-01'
    },
    iconSize: {
      type: [String, Number],
      default: 18
    },
    iconColor: {
      type: String,
      default: 'primary'
    },
    iconContainerSize: {
      type: [String, Number],
      default: 30
    },
    primaryTitle: {
      type: String,
      default: 'Click to upload'
    },
    secondaryTitle: {
      type: String,
      default: 'or drag and drop'
    },
    description: {
      type: String,
      default: 'JPG or PNG (max. 5MB)'
    },
    // Card styling props
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    height: {
      type: [String, Number],
      default: null
    },
    width: {
      type: [String, Number],
      default: null
    },
    minHeight: {
      type: [String, Number],
      default: 150
    },
    outlined: {
      type: Boolean,
      default: true
    },
    elevation: {
      type: [String, Number],
      default: 0
    },
    // Drag and drop styling
    dragOverBackgroundColor: {
      type: String,
      default: 'blue-100'
    },
    dragOverBorderColor: {
      type: String,
      default: 'blue-500'
    },
    // Upload strategy props
    uploadMode: {
      type: String,
      default: 'preview',
      validator: value => ['immediate', 'preview'].includes(value)
    },
    uploadUrl: {
      type: String,
      default: '/documents/'
    },
    uploadHeaders: {
      type: Object,
      default: () => ({})
    },
    uploadMethod: {
      type: String,
      default: 'POST'
    },
    // File size limit props
    fileSizeLimit: {
      type: Number,
      default: 5 // Default 5MB limit
    }
  },
  data() {
    return {
      isDragOver: false,
      uploadingFiles: new Map() // Track upload progress for each file
    }
  },
  computed: {
    cardClasses() {
      return {
        'yz-upload-card--disabled': this.disabled,
        'yz-upload-card--loading': this.loading || this.isUploading,
        'yz-upload-card--outlined': this.outlined,
        'yz-upload-card--drag-over': this.isDragOver,
        'yz-upload-card--uploading': this.isUploading,
        [`elevation-${this.elevation}`]: this.elevation > 0
      }
    },
    cardStyles() {
      const styles = {}
      if (this.height) styles.height = typeof this.height === 'number' ? `${this.height}px` : this.height
      if (this.width) styles.width = typeof this.width === 'number' ? `${this.width}px` : this.width
      if (this.minHeight) styles.minHeight = typeof this.minHeight === 'number' ? `${this.minHeight}px` : this.minHeight
      return styles
    },
    iconContainerStyles() {
      const size = typeof this.iconContainerSize === 'number' ? `${this.iconContainerSize}px` : this.iconContainerSize
      return {
        width: size,
        height: size
      }
    },
    dragOverStyles() {
      if (this.isDragOver) {
        const style = {
          transform: 'scale(1.02)',
          transition: 'all 0.2s ease'
        }

        // Handle drag-over background color
        if (this.dragOverBackgroundColor) {
          if (this.dragOverBackgroundColor.startsWith('#')) {
            style.backgroundColor = this.dragOverBackgroundColor
          } else if (this.dragOverBackgroundColor.startsWith('var(')) {
            style.backgroundColor = this.dragOverBackgroundColor
          } else if (this.dragOverBackgroundColor.startsWith('yz-')) {
            style.backgroundColor = `var(--${this.dragOverBackgroundColor})`
          } else {
            style.backgroundColor = `var(--yz-${this.dragOverBackgroundColor})`
          }
        }

        // Handle drag-over border color
        if (this.dragOverBorderColor) {
          if (this.dragOverBorderColor.startsWith('#')) {
            style.borderColor = this.dragOverBorderColor
          } else if (this.dragOverBorderColor.startsWith('var(')) {
            style.borderColor = this.dragOverBorderColor
          } else if (this.dragOverBorderColor.startsWith('yz-')) {
            style.borderColor = `var(--${this.dragOverBorderColor})`
          } else {
            style.borderColor = `var(--yz-${this.dragOverBorderColor})`
          }
        }

        return style
      }
      return {}
    },
    combinedStyles() {
      return {
        ...this.cardStyles,
        ...this.dragOverStyles
      }
    },
    isUploading() {
      return Array.from(this.uploadingFiles.values()).some(file => file.uploading)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    handleCardClick() {
      if (!this.disabled && !this.loading && !this.isUploading) {
        this.$refs.fileInput.click()
      }
    },
    handleDragOver(e) {
      if (!this.disabled && !this.loading && !this.isUploading) {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'copy'
      }
    },
    handleDragEnter(e) {
      if (!this.disabled && !this.loading && !this.isUploading) {
        e.preventDefault()
        this.isDragOver = true
      }
    },
    handleDragLeave(e) {
      if (!this.disabled && !this.loading && !this.isUploading) {
        e.preventDefault()
        // Only set isDragOver to false if we're leaving the card completely
        if (!e.currentTarget.contains(e.relatedTarget)) {
          this.isDragOver = false
        }
      }
    },
    handleDrop(e) {
      if (!this.disabled && !this.loading && !this.isUploading) {
        e.preventDefault()
        this.isDragOver = false

        const files = e.dataTransfer.files
        if (files.length > 0) {
          // Validate file types before processing
          const validFiles = this.validateFileTypes(files)
          if (validFiles.length > 0) {
            this.processFiles(validFiles)
          }
        }
      }
    },
    validateFileTypes(files) {
      const validFiles = []
      const invalidFiles = []

      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (this.isValidFileType(file)) {
          validFiles.push(file)
        } else {
          invalidFiles.push(file)
        }
      }

      // Show error message for invalid files
      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map(f => f.name).join(', ')
        const acceptedTypes = this.getAcceptedTypesMessage()
        this.showMessage(['error', `Invalid file type(s): ${fileNames}. ${acceptedTypes}`])
      }

      return validFiles
    },
    isValidFileType(file) {
      // Handle different accept formats
      if (!this.accept || this.accept === '*') {
        return true
      }

      const acceptTypes = this.accept.split(',').map(type => type.trim())

      for (const acceptType of acceptTypes) {
        if (acceptType === '*/*') {
          return true
        }

        // Handle MIME type patterns like 'image/*'
        if (acceptType.includes('/*')) {
          const baseType = acceptType.split('/')[0]
          if (file.type.startsWith(baseType + '/')) {
            return true
          }
        }

        // Handle exact MIME types like 'image/jpeg'
        if (acceptType.includes('/') && file.type === acceptType) {
          return true
        }

        // Handle file extensions like '.jpg', '.png'
        if (acceptType.startsWith('.')) {
          const extension =
            '.' +
            file.name
              .split('.')
              .pop()
              .toLowerCase()
          if (extension === acceptType.toLowerCase()) {
            return true
          }
        }
      }

      return false
    },
    getAcceptedTypesMessage() {
      if (!this.accept || this.accept === '*') {
        return 'All file types are accepted.'
      }

      const acceptTypes = this.accept.split(',').map(type => type.trim())

      if (acceptTypes.includes('image/*')) {
        return 'Only image files are accepted.'
      } else if (acceptTypes.some(type => type.startsWith('image/'))) {
        const imageTypes = acceptTypes.filter(type => type.startsWith('image/')).map(type => type.split('/')[1].toUpperCase())
        return `Only ${imageTypes.join(', ')} image files are accepted.`
      } else if (acceptTypes.every(type => type.startsWith('.'))) {
        const extensions = acceptTypes.map(type => type.toUpperCase())
        return `Only ${extensions.join(', ')} files are accepted.`
      } else {
        return `Accepted types: ${acceptTypes.join(', ')}`
      }
    },
    async processFiles(files) {
      const processedFiles = []
      const uploadPromises = []

      if (!files || files.length === 0) {
        return
      }

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]
          if (!file) continue // Skip if file is undefined/null

          const fileId = `file_${Date.now()}_${i}`

          // Check file size limit
          if (isFileSizeOverLimit(file, this.fileSizeLimit)) {
            await this.showMessage(['error', `${file.name}: ${this.$t('error_message.file_reached_limit')}`])
            continue // Skip this file and continue with others
          }
          // Handle upload based on mode
          if (this.uploadMode === 'immediate') {
            let fileToUpload = file

            // For image files, apply resizing
            if (file.type.startsWith('image/')) {
              try {
                const resizedImage = await resizeImage(file, file.name)
                fileToUpload = resizedImage.file
              } catch (error) {
                console.error('Failed to resize image:', error)
                await this.showMessage(['error', `${file.name}: error`])
                continue // Skip this file on error
              }
            }

            // Immediate upload mode - create file object first, then upload
            const fileObj = {
              fileId,
              file: fileToUpload,
              filename: fileToUpload.name,
              size: fileToUpload.size,
              type: fileToUpload.type,
              uploaded: false,
              uploading: true,
              progress: 0,
              error: null
            }

            // Start upload in background and update the file object
            const uploadPromise = this.uploadToApi(fileToUpload, fileId, fileObj)
              .then(uploadResponse => {
                // Update file object with upload response
                Object.assign(fileObj, {
                  uploaded: true,
                  uploading: false,
                  progress: 100,
                  ...uploadResponse
                })
                return { success: true, fileId, file: fileToUpload, response: uploadResponse }
              })
              .catch(error => {
                console.error('Failed to upload file:', error)
                Object.assign(fileObj, {
                  uploaded: false,
                  uploading: false,
                  progress: 0,
                  error: error
                })
                return { success: false, fileId, file: fileToUpload, error }
              })

            // Store upload promise for batch processing
            processedFiles.push(fileObj)
            uploadPromises.push(uploadPromise)
          } else {
            // Preview mode - create blob URLs (current behavior)
            if (file.type.startsWith('image/')) {
              try {
                const resizedImage = await resizeImage(file, file.name)
                processedFiles.push({
                  fileId,
                  blob: resizedImage.dataURL,
                  file: resizedImage.file,
                  filename: resizedImage.file.name,
                  size: resizedImage.file.size,
                  type: resizedImage.file.type,
                  uploaded: false
                })
              } catch (error) {
                console.error('Failed to resize image:', error)
                await this.showMessage(['error', `${file.name}: error`])
                continue // Skip this file on error
              }
            } else {
              // For non-image files, don't create a blob preview
              processedFiles.push({
                fileId,
                blob: null,
                file: file,
                filename: file.name,
                size: file.size,
                type: file.type,
                uploaded: false
              })
            }
          }

          // If not multiple, break after first file
          if (!this.multiple) {
            break
          }
        }

        // Handle batch upload completion for immediate mode
        if (this.uploadMode === 'immediate' && uploadPromises.length > 0) {
          // Wait for all uploads to complete
          Promise.allSettled(uploadPromises)
            .then(results => {
              // Extract successful upload responses
              const successfulUploads = results
                .filter(result => result.status === 'fulfilled' && result.value.success)
                .map(result => result.value.response)

              if (successfulUploads.length > 0) {
                this.$emit('upload-batch-complete', successfulUploads)
              }
            })
        }

        // Handle file output based on mode
        let result
        if (this.multiple) {
          if (this.appendFiles && this.value && this.value.length > 0) {
            // Append new files to existing ones
            result = [...this.value, ...processedFiles]
          } else {
            // Replace with new files
            result = processedFiles
          }
        } else {
          // For single file mode, always return array of length 1
          result = [processedFiles[0]]
        }
        this.$emit('input', result)
      } catch (error) {
        console.error('Error processing files:', error)
        this.$emit('error', error)
      }
    },
    async uploadToApi(file, fileId, fileObj = null) {
      if (!file) {
        return Promise.reject(new Error('File is required for upload'))
      }

      return new Promise((resolve, reject) => {
        const formData = new FormData()
        formData.append('file', file)

        // Initialize upload tracking
        this.uploadingFiles.set(fileId, { progress: 0, uploading: true })
        this.$emit('upload-start', { file, fileId })

        const headers = {
          'Content-Type': 'multipart/form-data',
          ...this.uploadHeaders
        }

        this.$axios({
          method: this.uploadMethod,
          url: this.uploadUrl,
          data: formData,
          headers,
          onUploadProgress: progressEvent => {
            let progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            if (progress === 100) {
              // Let the 'then' handler set to 100% to avoid premature completion
              progress = 96
            }
            this.uploadingFiles.set(fileId, { progress, uploading: true })

            // Update the file object directly if provided
            if (fileObj) {
              fileObj.progress = progress
            }

            this.$emit('upload-progress', { file, fileId, progress })
          }
        })
          .then(response => {
            // File uploaded to primary endpoint
            const uploadResponse = response.data
            this.uploadingFiles.set(fileId, { progress: 100, uploading: false, completed: true })
            this.$emit('upload-success', { file, fileId, response: uploadResponse })
            resolve(uploadResponse)
          })
          .catch(error => {
            this.uploadingFiles.set(fileId, { progress: 0, uploading: false, error: true })
            this.$emit('upload-error', { file, fileId, error })
            reject(error)
          })
      })
    },
    async onFileChange(e) {
      const files = e.target.files
      if (!files.length) {
        return
      }

      // Note: File input validation is already handled by the accept attribute
      // So we don't need to validate here, but we could add additional validation if needed
      await this.processFiles(files)

      // Clear the input to allow re-selecting the same file
      e.target.value = ''
    }
  }
}
</script>

<style scoped>
.yz-upload-card {
  position: relative;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 200px;
  background-color: #ffffff;
}

.yz-upload-card:hover {
  background-color: var(--yz-neutral-200, #f5f5f5);
}

.yz-upload-card--outlined {
  border: 2px dashed #d0d0d0;
}

.yz-upload-card--disabled {
  cursor: not-allowed;
  opacity: 0.6;
  background-color: #fafafa;
}

.yz-upload-card--disabled:hover {
  background-color: #fafafa;
}

.yz-upload-card--loading {
  cursor: wait;
}

.yz-upload-card--uploading {
  cursor: wait;
  opacity: 0.8;
}

.yz-upload-card__content {
  padding: 24px;
  width: 100%;
}

.yz-upload-card__icon {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--yz-neutral-300);
  border-radius: 50%;
  margin: 0 auto;
}

.yz-upload-card__title {
  font-weight: 500;
}

.yz-upload-card__description {
  opacity: 0.7;
}

.yz-upload-input {
  position: absolute;
  left: -9999px;
  opacity: 0;
  pointer-events: none;
}

/* Elevation classes */
.elevation-1 {
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24);
}

.elevation-2 {
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23);
}

.elevation-3 {
  box-shadow: 0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23);
}

.elevation-4 {
  box-shadow: 0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22);
}

.elevation-5 {
  box-shadow: 0px 19px 38px rgba(0, 0, 0, 0.3), 0px 15px 12px rgba(0, 0, 0, 0.22);
}
</style>
